import { useMemo, useState, type SyntheticEvent } from 'react';
import { Spin, Switch } from 'antd';
import type { SensorReportShipment } from '@/apis/shipmentEvents';
import type { TelemetryVital } from '@/apis/telemetry';
import { CustomModalWithoutButton } from '@/components';
import { getUserTimeZone } from '@/common/utils/utils';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import ChartPicker from './components/ChartPicker';
import ChartStack from './components/ChartStack';
import Legend from './components/Legend';
import PdfPreview from './components/PdfPreview';
import RangeMenu from './components/RangeMenu';
import { shippingEvents } from './lib/events';
import { reportRangeName } from './lib/report';
import { ChunkStatus, useShipmentReportDetail } from './queries';
import { TimelineStatus, useSensorTimeline } from './useSensorTimeline';
import styles from './SensorTimelineModal.module.css';

const MODAL_WIDTH = 'min(1280px, calc(100vw - 40px))';
const MOBILE_MODAL_WIDTH = '100%';

const keepInSheet = (event: SyntheticEvent<HTMLElement>): void => {
  if (event.currentTarget.closest('[data-rsbs-root]')) event.stopPropagation();
};

const STATUS_MESSAGES: Record<
  | TimelineStatus.Failed
  | TimelineStatus.Forbidden
  | TimelineStatus.NoData
  | TimelineStatus.Empty,
  string
> = {
  [TimelineStatus.Failed]: "Couldn't load sensor readings.",
  [TimelineStatus.Forbidden]:
    "You don't have permission to view this shipment's sensor data.",
  [TimelineStatus.NoData]:
    'No sensor data has been reported for this shipment.',
  [TimelineStatus.Empty]: 'No sensor readings between pickup and delivery.',
};

/** Props for {@link SensorTimelineModal}. */
export interface SensorTimelineModalProps {
  shipmentId: string;
  shareToken?: string;
  shipment?: SensorReportShipment;
  focus?: TelemetryVital;
  onClose: () => void;
}

/**
 * The Sensor modal: every attached device's readings as a stack of charts over the operational window or the full
 * data range, with the shipment's carrier scans drawn on every chart while "Show shipping events" is on. On a phone
 * it's a full-width sheet with the Charts picker and then the events toggle on their own rows (PRD §4.1). "Download
 * pdf" previews a sensor report of the charts as shown. Opened without the drawer's detail, it fetches its own, and
 * the events toggle and the report wait for it. Opened from the shipment page's mobile bottom sheet, presses stay
 * inside the modal so the sheet's drag gesture never sees them, and the scrolling body is exempt from the sheet's iOS
 * scroll lock. Opened from a vital, its chart is added to the saved charts when missing, then scrolled to and focused.
 */
const SensorTimelineModal = ({
  shipmentId,
  shareToken,
  shipment,
  focus,
  onClose,
}: SensorTimelineModalProps) => {
  const timeline = useSensorTimeline(shipmentId, shareToken, focus);
  const { status, view } = timeline;
  const { isMobile } = useBreakpoints();
  const [previewing, setPreviewing] = useState(false);
  const fetched = useShipmentReportDetail(shipmentId, {
    enabled: !shipment,
    shareToken,
  });
  const detail = shipment ?? fetched.data;
  const events = useMemo(
    () => (detail ? shippingEvents(detail.chain_of_custody ?? []) : []),
    [detail]
  );
  const showEvents = timeline.showEvents && Boolean(detail);
  const chartsLoading = timeline.charts.some(
    (key) =>
      (timeline.chartStatus[key] ?? ChunkStatus.Loading) === ChunkStatus.Loading
  );

  const picker = timeline.available.length > 0 && (
    <ChartPicker
      available={timeline.available}
      shown={timeline.charts}
      onApply={timeline.applyCharts}
      fullWidth={isMobile}
    />
  );
  const eventsToggle = status === TimelineStatus.Ready && (
    <label className={styles.eventsToggle}>
      <Switch
        size='small'
        checked={showEvents}
        disabled={!detail}
        onChange={timeline.setShowEvents}
      />
      Show shipping events
    </label>
  );
  const header = (
    <div className={styles.controls}>
      {!isMobile && eventsToggle}
      {!isMobile && picker}
      {status === TimelineStatus.Ready && (
        <RangeMenu
          range={timeline.range}
          onChange={timeline.setRange}
          onDownload={() => setPreviewing(true)}
          downloadDisabled={!detail || chartsLoading}
        />
      )}
    </div>
  );

  return (
    <div
      className={styles.eventBoundary}
      onPointerDown={keepInSheet}
      onMouseDown={keepInSheet}
      onTouchStart={keepInSheet}
    >
      <CustomModalWithoutButton
        modalOpen
        setModalOpen={onClose}
        modalTitle='Sensor'
        modalText=''
        modalSize={isMobile ? MOBILE_MODAL_WIDTH : MODAL_WIDTH}
        fontSize='24px'
        lineHeight='32px'
        headerExtra={header}
        headerExtraBottom={
          isMobile && (picker || eventsToggle) ? (
            <div className={styles.mobileRows}>
              {picker && <div className={styles.mobilePicker}>{picker}</div>}
              {eventsToggle}
            </div>
          ) : undefined
        }
      >
        <div className={styles.body} data-body-scroll-lock-ignore=''>
          {status === TimelineStatus.Loading && (
            <div className={styles.placeholder}>
              <Spin size='large' />
            </div>
          )}
          {status !== TimelineStatus.Loading &&
            status !== TimelineStatus.Ready && (
              <div className={styles.placeholder} role='status'>
                {STATUS_MESSAGES[status]}
              </div>
            )}
          {status === TimelineStatus.Ready && view && (
            <ChartStack
              key={timeline.range}
              charts={timeline.charts}
              chartStatus={timeline.chartStatus}
              devices={timeline.devices}
              series={timeline.series}
              view={view}
              notLoaded={timeline.notLoaded}
              hidden={timeline.hidden}
              timeZone={getUserTimeZone().name}
              events={events}
              showEvents={showEvents}
              focusChart={timeline.focusChart}
              onChartFocused={timeline.chartFocused}
              renderReport={(range) =>
                previewing &&
                detail && (
                  <PdfPreview
                    shipment={detail}
                    rangeName={reportRangeName(timeline.range, range.zoomed)}
                    view={view}
                    range={range}
                    charts={timeline.charts}
                    chartStatus={timeline.chartStatus}
                    devices={timeline.devices}
                    series={timeline.series}
                    notLoaded={timeline.notLoaded}
                    hidden={timeline.hidden}
                    events={events}
                    timeZone={getUserTimeZone().name}
                    onClose={() => setPreviewing(false)}
                  />
                )
              }
            />
          )}
        </div>
        {status === TimelineStatus.Ready && (
          <div className={styles.footer}>
            <Legend
              devices={timeline.devices}
              hidden={timeline.hidden}
              onToggle={timeline.toggleDevice}
            />
          </div>
        )}
      </CustomModalWithoutButton>
    </div>
  );
};

export default SensorTimelineModal;
